import { GroupRelationshipType, Role, Domain } from '@policyfly/protobuf/pfbingo'
import {
  ACCOUNTING_ROLES,
  API_ROLES,
  CAPTURE_ROLES,
  INTERNAL_ROLES,
  PARTNER_ROLES,
  REPORTING_ROLES,
} from '@policyfly/utils/constants'

export const prettyRoles = new Map<Role, string>([
  [Role.ROLE_AGENCY_READ_ONLY, 'Read Only (Agency)'],
  [Role.ROLE_AUDITOR, 'Auditor'],
  [Role.ROLE_AUDITOR_GLOBAL, 'Read Only - External'],
  [Role.ROLE_DISTRIBUTION_PARTNER, 'Distribution Partner'],
  [Role.ROLE_ISSUING_AGENT, 'Issuing Agent'],
  [Role.ROLE_PROGRAM_ADMIN, 'Program Admin'],
  [Role.ROLE_PROGRAM_READ_ONLY, 'Read Only (Program)'],
  [Role.ROLE_QUALITY_CONTROL, 'Quality Control'],
  [Role.ROLE_STAFF, 'Staff'],
  [Role.ROLE_SUPERUSER, 'Superuser'],
  [Role.ROLE_SYSTEM_READ_ONLY, 'Read Only (System)'],
  [Role.ROLE_TECHNICIAN, 'Technician'],
  [Role.ROLE_UNDERWRITER, 'Underwriter'],
  [Role.ROLE_QUOTING_ISSUING_AGENT, 'Quoting + Issuing Agent'],
  [Role.ROLE_CAPTURE_READ_ONLY, 'Read Only (Capture)'],
  [Role.ROLE_CAPTURE_READ_WRITE, 'Read + Write (Capture)'],
  [Role.ROLE_ACCOUNTING_READ_ONLY, 'Read Only (Accounting)'],
  [Role.ROLE_ACCOUNTING_READ_WRITE, 'Read + Write (Accounting)'],
  [Role.ROLE_API_AGENCY_ADMIN, 'Agency Admin (API)'],
  [Role.ROLE_API_OWNER_ADMIN, 'Owner Admin (API)'],
  [Role.ROLE_REPORTING_READ_ONLY, 'Read Only (Reporting)'],
  [Role.ROLE_UNSPECIFIED, 'Unspecified'],
])

export const prettyGroupRelationshipTypes = new Map<GroupRelationshipType, string>([
  [GroupRelationshipType.GROUP_RELATIONSHIP_TYPE_UNSPECIFIED, 'Unspecified'],
  [GroupRelationshipType.GROUP_RELATIONSHIP_TYPE_OWNER, 'Owner'],
  [GroupRelationshipType.GROUP_RELATIONSHIP_TYPE_DISTRIBUTION_PARTNER, 'Distribution Partner'],
])

/**
 * Program-level roles bingo accepts for each {@link Domain}.
 * Mirrors `storage.ProgramRolesByDomain` in pfbingo, which rejects any
 * `AddUsersToProgram` / `AddUserToGroup` call whose role is not in the domain's list.
 */
const programRolesByDomain = new Map<Domain, readonly Role[]>([
  [Domain.DOMAIN_PF_CORE, [...PARTNER_ROLES, ...INTERNAL_ROLES]],
  [Domain.DOMAIN_CAPTURE, CAPTURE_ROLES],
  [Domain.DOMAIN_ACCOUNTING, ACCOUNTING_ROLES],
  [Domain.DOMAIN_API, API_ROLES],
  [Domain.DOMAIN_REPORTING, REPORTING_ROLES],
])

/**
 * Program roles a user can be granted through a group in the given domain.
 *
 * PF Core additionally splits its roles by the group's relationship to the program
 * (partner groups get agent roles, owner groups get internal roles).
 * Other domains have a single flat list regardless of relationship.
 */
export function programRolesForDomain (domain: Domain, relationshipType: GroupRelationshipType | null): readonly Role[] {
  if (domain === Domain.DOMAIN_PF_CORE) {
    switch (relationshipType) {
      case GroupRelationshipType.GROUP_RELATIONSHIP_TYPE_DISTRIBUTION_PARTNER: return PARTNER_ROLES
      case GroupRelationshipType.GROUP_RELATIONSHIP_TYPE_OWNER: return INTERNAL_ROLES
      default: return []
    }
  }
  if (!relationshipType) return []
  return programRolesByDomain.get(domain) ?? []
}

export function canPinReleaseVersion (role: Role | undefined, environmentName: string | undefined): boolean {
  if (!role || !environmentName) return false
  if (environmentName.toLowerCase() === 'prod') return role === Role.ROLE_SUPERUSER
  return role === Role.ROLE_STAFF || role === Role.ROLE_SUPERUSER
}
